import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import loader from "../assets/loader.json";
import { useTranscationReportMutation } from "../redux/features/user/userApi";
import Navbar from "../Components/Navbar";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval } from "date-fns";
import * as XLSX from "xlsx";

const ReportTrans = () => {
  const navigate = useNavigate();
  const [transcationReport, { isSuccess, isError, isLoading, data, error }] = useTranscationReportMutation();
  const [reportData, setReportData] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  // Date filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // Interval state for graph
  const [interval, setInterval] = useState("day"); // day, week, month, year
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
  // Checkbox: whether to apply interval filter to the table
  const [applyIntervalToTable, setApplyIntervalToTable] = useState(true);

  useEffect(() => {
    // Fetch transaction report when component mounts
    const fetchData = async () => {
      try {
        await transcationReport({ type: interval }).unwrap();
      } catch (error) {
        console.error("Error fetching transaction report:", error);
      }
    };
    fetchData();
  }, [transcationReport, interval]);

  useEffect(() => {
    // Update report data when API data is received
    console.log("API Response:", { isSuccess, isError, isLoading, data });
    if (isSuccess && data?.data) {
      console.log("Setting report data:", data.data);
      setReportData(data.data);
    } else if (isSuccess && data) {
      console.log("Data structure:", data);
      // Try setting data directly if it's not nested under 'data'
      setReportData(data);
    }
  }, [isSuccess, data]);

  // Format date for table based on selected interval
  const formatDateForTable = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);

    if (interval === 'week') {
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      const today = new Date();
      const todayWeekStart = new Date(today);
      todayWeekStart.setDate(today.getDate() - today.getDay());
      const todayWeekEnd = new Date(todayWeekStart);
      todayWeekEnd.setDate(todayWeekStart.getDate() + 6);

      const isCurrentWeek = weekStart.toDateString() === todayWeekStart.toDateString() &&
        weekEnd.toDateString() === todayWeekEnd.toDateString();

      const range = `${format(weekStart, 'MMM d')} – ${format(weekEnd, 'MMM d')}`;
      return isCurrentWeek ? `${range} (Current week)` : range;
    }

    if (interval === 'month') {
      // Show full date in month interval
      return format(date, 'MMM d, yyyy');
    }

    if (interval === 'year') {
      return format(date, 'yyyy');
    }

    // default: day
    return format(date, 'MMM d, yyyy');
  };

  // Filter transactions based on search and date range
  // Handle different possible data structures
  const transactionsArray = reportData?.transactions || reportData || [];

  // Helper: compare only date portion (local time)
  const isSameLocalDay = (a, b) => {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  };

  // Convert dd/mm/yyyy -> yyyy-MM-dd for input[type="date"] value
  const toYyyyMmDdFromDdMmYyyy = (value) => {
    const d = parseDdMmYyyy(value);
    if (!d) return "";
    const yyyy = String(d.getFullYear());
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Convert yyyy-MM-dd -> dd/mm/yyyy when user picks from calendar
  const toDdMmYyyyFromYyyyMmDd = (value) => {
    if (!value) return "";
    const parts = value.split('-');
    if (parts.length !== 3) return "";
    const [yyyy, mm, dd] = parts;
    return `${dd.padStart(2,'0')}/${mm.padStart(2,'0')}/${yyyy}`;
  };

  // Parse dd/mm/yyyy into Date (start of day)
  const parseDdMmYyyy = (value) => {
    if (!value || typeof value !== 'string') return null;
    const parts = value.split('/');
    if (parts.length !== 3) return null;
    const [dd, mm, yyyy] = parts.map((p) => p.trim());
    const day = Number(dd);
    const monthIndex = Number(mm) - 1;
    const year = Number(yyyy);
    if (!Number.isFinite(day) || !Number.isFinite(monthIndex) || !Number.isFinite(year)) return null;
    const d = new Date(year, monthIndex, day);
    if (isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Base filter across search and common date range (dd/mm/yyyy)
  const baseFiltered = Array.isArray(transactionsArray)
    ? transactionsArray.filter((txn) => {
          const query = search.trim().toLowerCase();
          const matchesSearch =
            !query ||
            String(txn.email ?? "").toLowerCase().includes(query) ||
            String(txn.tokenID ?? "").toLowerCase().includes(query) ||
            String(txn.mode ?? txn.currency ?? "").toLowerCase().includes(query) ||
            (txn.date && new Date(txn.date).toLocaleDateString("en-US").toLowerCase().includes(query));

        if (startDate) {
          const sd = parseDdMmYyyy(startDate);
          if (sd && txn.date && new Date(txn.date) < sd) return false;
        }
        if (endDate) {
          const ed = parseDdMmYyyy(endDate);
          if (ed) {
            const inclusiveEnd = new Date(ed);
            inclusiveEnd.setHours(23, 59, 59, 999);
            if (txn.date && new Date(txn.date) > inclusiveEnd) return false;
          }
        }
        return matchesSearch;
      })
    : [];

  // Apply interval to table (month uses selectedMonth for table)
  const filteredTransactions = baseFiltered
    .filter((txn) => {
      if (!applyIntervalToTable) return true;
            if (!txn.date) return false;
            const txnDate = new Date(txn.date);

      if (interval === 'day') {
        const today = new Date();
        return isSameLocalDay(txnDate, today);
      }
      if (interval === 'week') {
            const today = new Date();
            const currentWeekStart = new Date(today);
            currentWeekStart.setHours(0,0,0,0);
            currentWeekStart.setDate(today.getDate() - today.getDay());
            const currentWeekEnd = new Date(currentWeekStart);
            currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
            currentWeekEnd.setHours(23,59,59,999);
        return txnDate >= currentWeekStart && txnDate <= currentWeekEnd;
      }
      if (interval === 'month') {
        if (!selectedMonth) return true;
        const [yearStr, monthStr] = selectedMonth.split('-');
        const year = Number(yearStr);
        const monthIndex = Number(monthStr) - 1;
        const monthStart = new Date(year, monthIndex, 1);
        const monthEnd = endOfMonth(monthStart);
        return txnDate >= monthStart && txnDate <= monthEnd;
      }
      if (interval === 'year') {
            const now = new Date();
        return txnDate.getFullYear() === now.getFullYear();
      }
      return true;
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  // Debug logging
  console.log("Report Data:", reportData);
  console.log("Filtered Transactions:", filteredTransactions);

  // Graph should not use selectedMonth; it uses base filters only and interval (except month constraint)
  const graphTransactions = React.useMemo(() => {
    return baseFiltered.filter((txn) => {
      if (!txn?.date) return false;
      const txnDate = new Date(txn.date);
      if (interval === 'day') {
        const today = new Date();
        return isSameLocalDay(txnDate, today);
      }
      if (interval === 'week') {
        const today = new Date();
        const currentWeekStart = new Date(today);
        currentWeekStart.setHours(0,0,0,0);
        currentWeekStart.setDate(today.getDate() - today.getDay());
        const currentWeekEnd = new Date(currentWeekStart);
        currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
        currentWeekEnd.setHours(23,59,59,999);
        return txnDate >= currentWeekStart && txnDate <= currentWeekEnd;
      }
      if (interval === 'year') {
        const now = new Date();
        return txnDate.getFullYear() === now.getFullYear();
      }
      // interval === 'month' or others: don't constrain graph by selected month
      return true;
    });
  }, [baseFiltered, interval]);

  // --- Transaction Graph Data ---
  const groupedGraphData = React.useMemo(() => {
    const group = {};
    graphTransactions.forEach((txn) => {
      if (!txn.date) return;
      const dateObj = new Date(txn.date);
      let key = "";
      if (interval === "day") {
        key = format(dateObj, "yyyy-MM-dd");
      } else if (interval === "week") {
        const weekStart = new Date(dateObj);
        weekStart.setDate(dateObj.getDate() - dateObj.getDay());
        key = format(weekStart, "yyyy-MM-dd");
      } else if (interval === "month") {
        key = format(dateObj, "yyyy-MM");
      } else if (interval === "year") {
        key = format(dateObj, "yyyy");
      }
      if (!group[key]) {
        group[key] = { count: 0, amount: 0 };
      }
      group[key].count += 1;
      group[key].amount += txn.amount || 0;
    });
    return group;
  }, [graphTransactions, interval]);

  // Chart options
  const chartOptions = {
    chart: {
      zoomType: 'x',
      backgroundColor: 'transparent',
      spacing: [16, 16, 16, 16],
    },
    title: {
      text: `Transaction Report — ${interval.charAt(0).toUpperCase() + interval.slice(1)} view`,
      style: { fontSize: '16px', fontWeight: '600' }
    },
    xAxis: {
      categories: Object.keys(groupedGraphData).sort(),
      title: { text: interval.charAt(0).toUpperCase() + interval.slice(1) },
      tickLength: 0,
      lineColor: '#e5e7eb',
      labels: { style: { color: '#6b7280' } },
    },
    yAxis: [
      {
        title: { text: 'Transactions' },
        gridLineColor: '#f3f4f6',
        labels: { style: { color: '#6b7280' } },
      },
      {
        title: { text: 'Amount' },
        gridLineColor: '#f3f4f6',
        labels: {
          style: { color: '#6b7280' },
          formatter: function () {
            const v = Number(this.value) || 0;
            return v >= 1000 ? `${(v/1000).toFixed(1)}k` : `${v}`;
          }
        },
        opposite: true,
      }
    ],
    plotOptions: {
      series: {
        animation: { duration: 300 },
        marker: { enabled: false, radius: 3, symbol: 'circle' },
      },
      column: {
        borderRadius: 4,
        pointPadding: 0.08,
        groupPadding: 0.12,
        dataLabels: {
          enabled: true,
          style: { color: '#111827', textOutline: 'none' },
          formatter: function () {
            return this.y > 0 ? this.y : '';
          }
        }
      }
    },
    series: [
      {
        name: 'Transaction Count',
        type: 'column',
        data: Object.values(groupedGraphData).map(item => item.count),
        color: '#3B82F6'
      },
      {
        name: 'Total Amount',
        type: 'spline',
        yAxis: 1,
        data: Object.values(groupedGraphData).map(item => item.amount),
        color: '#10B981',
        lineWidth: 3,
        tooltip: {
          valueDecimals: 2
        }
      }
    ],
    tooltip: {
      shared: true,
      borderColor: '#e5e7eb',
      backgroundColor: '#ffffff',
      style: { color: '#111827' },
      formatter: function () {
        const p = this.points || [];
        const key = this.x;
        const count = p.find(pt => pt.series.name === 'Transaction Count');
        const amount = p.find(pt => pt.series.name === 'Total Amount');
        const amountVal = amount ? amount.y : 0;
        const amountStr = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amountVal);
        return `<b>${key}</b><br/>` +
          `${count ? `Count: <b>${count.y}</b><br/>` : ''}` +
          `${amount ? `Amount: <b>${amountStr}</b>` : ''}`;
      }
    },
    legend: {
      enabled: true,
      align: 'center',
      verticalAlign: 'bottom',
      itemStyle: { color: '#374151', fontWeight: '500' },
    },
    credits: { enabled: false },
    responsive: {
      rules: [
        {
          condition: { maxWidth: 640 },
          chartOptions: {
            legend: { layout: 'horizontal', align: 'center', verticalAlign: 'bottom' },
            yAxis: [{ title: { text: '' } }, { title: { text: '' } }],
          }
        }
      ]
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTransactions = filteredTransactions.slice(startIndex, endIndex);

  // Excel Export function (respects current date filters)
  const exportToExcel = () => {
    const rows = filteredTransactions.map((t) => ({
      Date: t.date ? format(new Date(t.date), "yyyy-MM-dd HH:mm") : "",
      Email: t.email || "",
      Mode: t.mode || t.currency || "",
      Amount: t.amount ?? 0,
    }));

    const sheet = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "Transactions");

    // Filename with date filter context (dd-mm-yyyy)
    const start = startDate ? startDate.replaceAll('/', '-') : "all";
    const end = endDate ? endDate.replaceAll('/', '-') : "all";
    const filename = `transaction-report_${start}_to_${end}.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Lottie animationData={loader} className="w-32 h-32" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            Error loading transaction report. Please try again.
            {error?.data?.message && (
              <div className="mt-2 text-sm text-red-600">{String(error.data.message)}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="w-full max-w-screen-2xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6 md:py-8" id="report-content">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Transaction Report</h1>
          <p className="text-gray-600">Comprehensive transaction analysis and reporting</p>
        </div>

        {/* Summary Cards (Unfiltered Totals) */}
        {reportData && (
          <>
            {(() => {
              const baseTransactions = Array.isArray(transactionsArray) ? transactionsArray : [];
              const baseTotalTransactions = reportData.totalTransactions ?? baseTransactions.length ?? 0;
              const baseTotalAmount = reportData.totalAmount ?? baseTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);
              const baseTotalUsers = reportData.activeUsers ?? new Set(baseTransactions.map(t => t.email)).size;
              return (
                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6 mb-8">
                  <div className="bg-white/80 backdrop-blur rounded-xl shadow-lg border border-gray-100 p-4 sm:p-5 md:p-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Total Transactions</h3>
                    <p className="text-3xl font-bold text-blue-600">{baseTotalTransactions}</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur rounded-xl shadow-lg border border-gray-100 p-4 sm:p-5 md:p-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Total SNFT</h3>
                    <p className="text-3xl font-bold text-green-600">{baseTotalAmount}</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur rounded-xl shadow-lg border border-gray-100 p-4 sm:p-5 md:p-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Total Users</h3>
                    <p className="text-3xl font-bold text-purple-600">{baseTotalUsers}</p>
                  </div>
                </div>
              );
            })()}

            {/* Filtered Totals (Shown only when filters are applied) */}
            {(() => {
              const isFilterApplied =
                Boolean(search.trim()) || Boolean(startDate) || Boolean(endDate) ||
                (applyIntervalToTable && ["day", "week", "month", "year"].includes(interval));
              if (!isFilterApplied) return null;
              const filteredTotalTransactions = filteredTransactions.length;
              const filteredTotalAmount = filteredTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);
              const filteredTotalUsers = new Set(filteredTransactions.map(t => t.email)).size;
              return (
                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6 mb-8">
                  <div className="bg-white/80 backdrop-blur rounded-xl shadow-lg border border-gray-100 p-4 sm:p-5 md:p-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Total No of: Transactions</h3>
                    <p className="text-3xl font-bold text-blue-600">{filteredTotalTransactions}</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur rounded-xl shadow-lg border border-gray-100 p-4 sm:p-5 md:p-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Total No of: SNFT</h3>
                    <p className="text-3xl font-bold text-green-600">{filteredTotalAmount}</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur rounded-xl shadow-lg border border-gray-100 p-4 sm:p-5 md:p-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Total No of: Users</h3>
                    <p className="text-3xl font-bold text-purple-600">{filteredTotalUsers}</p>
                  </div>
                </div>
              );
            })()}
          </>
        )}
        
        {/* Debug Info - Remove this after debugging */}
        {/* {reportData && (
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-8">
            <h4 className="font-semibold text-yellow-800 mb-2">Debug Information:</h4>
            <pre className="text-xs text-yellow-700 overflow-auto">
              {JSON.stringify(reportData, null, 2)}
            </pre>
          </div>
        )} */}

        {/* Filters and Controls */}
        <div className="bg-white/90 backdrop-blur rounded-xl shadow-lg border border-gray-100 p-4 sm:p-5 md:p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search transactions..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date (mm/dd/yyyy)</label>
              <div className="relative">
                <input
                  type="text"
                  value={startDate}
                  readOnly
                  placeholder="dd/mm/yyyy"
    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="date"
                  value={toYyyyMmDdFromDdMmYyyy(startDate)}
                  onChange={(e) => setStartDate(toDdMmYyyyFromYyyyMmDd(e.target.value))}
                  className="absolute inset-0 w-full h-full  cursor-pointer"
                  aria-label="Select start date"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date (mm/dd/yyyy)</label>
              <div className="relative">
                <input
                  type="text"
                  value={endDate}
                  readOnly
                  placeholder="dd/mm/yyyy"
    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="date"
                  value={toYyyyMmDdFromDdMmYyyy(endDate)}
                  onChange={(e) => setEndDate(toDdMmYyyyFromYyyyMmDd(e.target.value))}
                  className="absolute inset-0 w-full h-full cursor-pointer"
                  aria-label="Select end date"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Interval</label>
              <select
                value={interval}
                onChange={async (e) => {
                  const next = e.target.value;
                  setInterval(next);
                  // Default to checked for any interval selection; user can uncheck to show all
                  setApplyIntervalToTable(true);
                  try {
                    await transcationReport({ type: next }).unwrap();
                  } catch (err) {
                    console.error("Interval change fetch error:", err);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
              </select>
            </div>
          </div>
          {/* Checkbox to toggle interval filter application to table */}
          <div className="mb-4">
            <label className="inline-flex items-center space-x-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={applyIntervalToTable}
                onChange={(e) => setApplyIntervalToTable(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span>
                {`Apply current ${interval} filter to table (uncheck to show all)`}
              </span>
            </label>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4">
            {interval === 'month' && (
            <div className="w-full sm:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-2">Month Filter</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={async () => {
                  try {
                    await transcationReport({ type: interval }).unwrap();
                  } catch (e) {
                    console.error("Refresh error:", e);
                  }
                }}
                className="w-full sm:w-auto px-5 sm:px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Refresh Data
              </button>
              <button
                onClick={exportToExcel}
                className="w-full sm:w-auto px-5 sm:px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Export Excel
              </button>
            </div>
          </div>
        </div>

        {/* Chart */}
        {/* {Object.keys(groupedGraphData).length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-8">
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          </div>
        )} */}

        {/* Transaction Table */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Transaction Details</h2>
          </div>
          <div className="overflow-x-auto">
            {filteredTransactions.length > 0 ? (
              <table className="min-w-[700px] w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-5 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 sm:px-5 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 sm:px-5 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User Name
                    </th>
                        <th className="px-4 sm:px-5 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User ID
                        </th>
                    <th className="px-4 sm:px-5 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th> */}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentTransactions.map((transaction, index) => (
                    <tr key={transaction._id || index} className="hover:bg-gray-50">
                      <td className="px-4 sm:px-5 md:px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDateForTable(transaction.date)}
                      </td>
                      <td className="px-4 sm:px-5 md:px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                        {transaction.email || 'N/A'}
                      </td>
                      <td className="px-4 sm:px-5 md:px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                        {transaction.username || 'N/A'}
                      </td>
                      <td className="px-4 sm:px-5 md:px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                        {transaction.ID || 'N/A'}
                      </td>
                      <td className="px-4 sm:px-5 md:px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                        {transaction.amount } {transaction.currency}
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          transaction.ordertype === 'completed' 
                            ? 'bg-green-100 text-green-800' 
                            : transaction.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {transaction.status || 'N/A'}
                        </span>
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="px-4 sm:px-5 md:px-6 py-6 sm:py-8 text-center">
                <div className="text-gray-500 text-lg mb-2">No transaction data available</div>
                <div className="text-gray-400 text-sm">
                  {reportData ? 'No transactions match your current filters.' : 'Loading transaction data...'}
                </div>
                {reportData && (
                  <div className="mt-4 text-xs text-gray-400">
                    Debug: Report data exists but no transactions found. Check console for details.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredTransactions.length)} of {filteredTransactions.length} results
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        page === currentPage
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportTrans;
